import React, { useState } from 'react';
import { useProjectTasksService } from 'nekst-api';
import { ActionCardState, PendingActionData } from '../../types';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import { EntityLink } from './EntityLink';
import styles from '../ChatWidget.module.scss';
import nekstAiIcon from './nekstAiIcon.png';

interface ActionCardProps {
  action: PendingActionData;
  isGrouped: boolean;
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={styles.actionCardSpinner}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function getStatusLabel(state: ActionCardState): string {
  if (state === 'applied') {
    return 'Description updated';
  } else if (state === 'rejected') {
    return 'Rejected';
  } else if (state === 'rolled-back') {
    return 'Rolled back';
  } else if (state === 'error') {
    return 'Failed to update';
  } else {
    return '';
  }
}

export default function ActionCard(props: ActionCardProps) {
  const { action, isGrouped } = props;
  const { isStreaming } = useChatWidgetContext();
  const projectTasksService = useProjectTasksService();
  const [cardState, setCardState] = useState<ActionCardState>('pending');

  const showAvatar = !isGrouped;
  const isLoading = cardState === 'applying' || cardState === 'rolling-back';

  const handleApply = async () => {
    setCardState('applying');
    try {
      await projectTasksService.updateDescription(action.payload.taskId, action.payload.proposedContent);
      setCardState('applied');
    } catch {
      setCardState('error');
    }
  };

  const handleReject = () => {
    setCardState('rejected');
  };

  const handleRollback = async () => {
    setCardState('rolling-back');
    try {
      await projectTasksService.updateDescription(action.payload.taskId, action.payload.originalContent);
      setCardState('rolled-back');
    } catch {
      setCardState('error');
    }
  };

  const statusLabel = getStatusLabel(cardState);

  return (
    <div
      className={`${styles.messageRow} ${styles.messageRowAssistant} ${isGrouped ? styles.messageRowGrouped : ''}`}
      data-testid="chat-action-card"
    >
      {showAvatar ? (
        <div className={styles.bubbleAvatar}>
          <img src={nekstAiIcon} alt="Nekst AI" width={28} height={28} />
        </div>
      ) : (
        <div className={styles.bubbleAvatarSpacer} />
      )}
      <div className={styles.bubbleWrapper}>
        <div className={styles.actionCard}>
          <div className={styles.actionCardHeader}>
            {'Proposed description update'}
            {action.payload.taskName ? (
              <>
                {' for '}
                <EntityLink type="task" id={action.payload.taskId} name={action.payload.taskName}>
                  {action.payload.taskName}
                </EntityLink>
              </>
            ) : null}
          </div>
          <div className={styles.actionCardContent}>
            <ChatMarkdownContent content={action.payload.proposedContent} />
          </div>
          <div className={styles.actionCardFooter}>
            {cardState === 'pending' && (
              <>
                <button
                  type="button"
                  className={styles.actionCardApplyBtn}
                  onClick={handleApply}
                  disabled={isStreaming}
                  data-testid="action-card-apply"
                >
                  <CheckIcon />
                  Apply
                </button>
                <button
                  type="button"
                  className={styles.actionCardRejectBtn}
                  onClick={handleReject}
                  data-testid="action-card-reject"
                >
                  <XIcon />
                  Reject
                </button>
              </>
            )}
            {isLoading && (
              <span className={styles.actionCardStatus}>
                <SpinnerIcon />
                {cardState === 'applying' ? 'Applying...' : 'Rolling back...'}
              </span>
            )}
            {cardState === 'applied' && (
              <>
                <span className={styles.actionCardStatusSuccess}>
                  <CheckIcon />
                  {statusLabel}
                </span>
                <button
                  type="button"
                  className={styles.actionCardRollbackBtn}
                  onClick={handleRollback}
                  disabled={isStreaming}
                  data-testid="action-card-rollback"
                >
                  <UndoIcon />
                  Rollback
                </button>
              </>
            )}
            {cardState === 'error' && (
              <>
                <span className={styles.actionCardStatusError}>
                  <XIcon />
                  {statusLabel}
                </span>
                <button
                  type="button"
                  className={styles.actionCardApplyBtn}
                  onClick={handleApply}
                  data-testid="action-card-retry"
                >
                  Retry
                </button>
              </>
            )}
            {(cardState === 'rejected' || cardState === 'rolled-back') && (
              <span className={styles.actionCardStatusMuted}>
                {statusLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
